import { Avatar, Box, Button, Link, Paper, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteSecurityPassportMutation, useUploadSecurityPassportMutation } from '../../../../hooks/mutations/useUserMutation';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import FileUploader from '../../../ui/FileUploader';
import Icon from '../../../ui/Icon';
import ModalBody from '../../ModalBody';

interface ModalProps {
    user: User | null;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateUserSecurityPassModal: React.FC<ModalProps> = ({ user, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState<File[]>([]);

    const { mutate: mutateSecurityPassport } = useUploadSecurityPassportMutation({ onSuccess: refetch });
    const { mutate: deleteSecurityPassport } = useDeleteSecurityPassportMutation({ onSuccess: refetch });

    const handleSubmit = () => {
        mutateSecurityPassport({ userId: user?._id || '', file: files[0] || undefined });
    };

    const handleDelete = () => {
        deleteSecurityPassport({ userId: user?._id || '' });
    };

    const handleFileUpload = (uploadedFiles: File[]) => setFiles(uploadedFiles);

    const isFormValid = () => {
        return !!files.length;
    };

    return (
        <ModalBody
            title={t('user')}
            subtitle={t('securityPassport')}
            description={formatName(user?.firstName, user?.lastName)}
            open={showEditModal}
            disableSubmit={!isFormValid()}
            handleClose={() => setShowEditModal(false)}
            onSubmit={handleSubmit}
            showSecondaryButton={!!user?.securityPassportFile}
            secondaryButtonText={t('delete')}
            onSecondaryAction={handleDelete}
        >
            {user?.securityPassportFile && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar variant="rounded" sx={{ width: 56, height: 56 }}>
                            <Icon name="insertDriveFile" />
                        </Avatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {user.securityPassportFile.fileName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {capitalizeFirstLetter(t('current'))}
                            </Typography>

                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Link href={user.securityPassportFile.url} target="_blank" rel="noopener">
                                    <Button size="small" startIcon={<Icon name="openInNew" />}>
                                        {capitalizeFirstLetter(t('open'))}
                                    </Button>
                                </Link>

                                {/*<IconButton iconName="delete" color="error" iconProps={{ fontSize: 'small' }} onClick={handleDelete} />*/}
                            </Stack>
                        </Box>
                    </Stack>
                </Paper>
            )}

            <FileUploader onUpload={handleFileUpload} files={files} accept=".pdf" />
        </ModalBody>
    );
};

export default UpdateUserSecurityPassModal;
